import type { Icon as PhosphorIcon } from "@phosphor-icons/react";
import {
  BookOpenText,
  Books,
  Compass,
  FingerprintSimple,
  Footprints,
  Heart,
  LinkSimple,
  Sun,
  UsersThree,
} from "@phosphor-icons/react";

export type TabId = string;

/**
 * Visual group for a sidebar row. Items in different groups are separated
 * by a horizontal divider when rendered by `SideNav`. `footer` items are
 * pinned to the bottom of the aside, just above the bottom gutter.
 */
export type NavItemGroup = "main" | "secondary" | "shared" | "utility" | "footer";

export interface DefaultNavItem {
  id: TabId;
  label: string;
  compactLabel?: string;
  path: string;
  Icon: PhosphorIcon;
  /**
   * The icon's colour, a `--color-vivid-*` token. Each destination keeps its
   * own hue so the rail reads at a glance instead of as one wall of teal.
   */
  tone?: string;
  /** Forces exact path matching for section roots that also have children. */
  end?: boolean;
  /** Optional uppercase label rendered above a run of related nav items. */
  sectionLabel?: string;
  /** Child rows rendered under a collapsible parent in the desktop sidebar. */
  children?: DefaultNavItem[];
  /** Greys the row + appends a "soon" tag. Used for in-build sub-pages. */
  comingSoon?: boolean;
  badge?: number;
  pulse?: boolean;
  hideOnMobile?: boolean;
  /**
   * Visual group. Items with different groups are separated by a divider.
   * Defaults to `main`. `footer` items render at the bottom of the aside
   * above the bottom gutter.
   */
  group?: NavItemGroup;
  /**
   * Skips React Router's `viewTransition` prop on this row's NavLink, so
   * navigating to it never starts the router's own, independent
   * `document.startViewTransition()`. On a cold visit to a page whose
   * previous screen is still asynchronously settling its own reveal
   * transition (useRevealSwap's ~500ms cleanup window after `.finished`),
   * that second transition can land while the first hasn't fully torn down
   * internally - a real WebKit crash (native SIGSEGV, confirmed via dtrace),
   * not a JS error. Set this on any destination proven to trigger it.
   * Currently: Snapshot.
   */
  disableViewTransition?: boolean;
}

/*
 * Sidebar items. /attention is intentionally NOT here - the v2.1 product
 * forbids feed-style surfaces ("There is no feed", §5.1). The single
 * Attention card lives on /today and is the entire attention model;
 * a sidebar item suggesting a list of pending attention events frames
 * the product the wrong way.
 */
/*
 * Flat, divider-separated groups. Nothing collapses: every destination is one
 * click away and the rail keeps its shape, the way Linear and Stripe treat
 * fixed navigation. A row is always a link, never also a toggle.
 *   main     - what you operate day to day: Today, Snapshot (and a coaching
 *              or care row when the member has one).
 *   secondary- spare slot between main and shared; nothing sits here by default.
 *   shared   - Explore, the hub page, then the places its cards lead to:
 *              Challenges, Community, Collection. The Explore row heads the
 *              group, so it needs no label of its own.
 *   utility  - labelled "Tools": Logbook, Connectors, Anatomy, then the
 *              runtime-only Contact Centre and Operations.
 *              Notifications live with the account menu in the top navigation.
 */

export const DEFAULT_NAV_ITEMS: DefaultNavItem[] = [
  {
    id: "ambient",
    label: "Today",
    compactLabel: "Today",
    path: "/",
    Icon: Sun,
    tone: "var(--color-vivid-amber)",
  },

  {
    id: "snapshot",
    label: "Snapshot",
    path: "/snapshot",
    Icon: Heart,
    tone: "var(--color-vivid-rose)",
    disableViewTransition: true,
  },
  {
    id: "explore",
    label: "Explore",
    compactLabel: "Explore",
    path: "/explore",
    Icon: Compass,
    tone: "var(--color-vivid-sky)",
    group: "shared",
    // Exact match, so Explore lights only on the hub, not under Collection.
    end: true,
  },
  {
    id: "challenges",
    label: "Challenges",
    compactLabel: "Steps",
    path: "/challenges",
    Icon: Footprints,
    tone: "var(--color-vivid-orange)",
    group: "shared",
  },
  {
    id: "community",
    label: "Community",
    compactLabel: "Friends",
    path: "/community",
    Icon: UsersThree,
    tone: "var(--color-vivid-purple)",
    hideOnMobile: true,
    group: "shared",
  },
  {
    id: "library",
    label: "Collection",
    compactLabel: "Saved",
    path: "/explore/collection",
    Icon: Books,
    tone: "var(--color-vivid-green)",
    hideOnMobile: true,
    group: "shared",
  },
  {
    id: "logbook",
    label: "Logbook",
    path: "/logbook",
    Icon: BookOpenText,
    tone: "var(--color-vivid-blue)",
    hideOnMobile: true,
    group: "utility",
    sectionLabel: "Tools",
  },
  {
    id: "connectors",
    label: "Connectors",
    compactLabel: "Links",
    path: "/connectors",
    Icon: LinkSimple,
    tone: "var(--color-vivid-periwinkle)",
    hideOnMobile: true,
    group: "utility",
    sectionLabel: "Tools",
  },
  {
    id: "anatomy",
    label: "Anatomy",
    compactLabel: "Anatomy",
    path: "/anatomy",
    Icon: FingerprintSimple,
    tone: "var(--color-vivid-pink)",
    hideOnMobile: true,
    group: "utility",
    sectionLabel: "Tools",
  },
];
